<template>
  <PreviewFrame :accent="slide.accent">
    <template #presenter>
      <div class="vh-question">
        <span class="vh-bar vh-bar--xl"></span>
      </div>
      <div class="vh-stage">
        <div class="vh-video">
          <svg viewBox="0 0 200 120" class="vh-frame" preserveAspectRatio="none">
            <rect width="200" height="120" fill="#1A1A2E" />
            <circle
              cx="100"
              cy="46"
              r="18"
              fill="#FFD6E7"
            />
            <path d="M40 110 Q100 80 160 110 L160 120 L40 120 Z" fill="#6A1EBB" opacity="0.5" />
            <rect
              x="84"
              y="64"
              width="32"
              height="6"
              fill="#FFE680"
            />
          </svg>
          <button class="vh-pause" tabindex="-1">
            <span class="material-symbols-rounded">pause</span>
          </button>
          <div class="vh-progress">
            <div class="vh-progress-fill"></div>
          </div>
        </div>
        <div class="vh-stream">
          <span
            v-for="(r, i) in stream"
            :key="i"
            class="vh-emoji"
            :style="{ '--em-i': i, '--x': r.x + '%' }"
          >{{ r.emoji }}</span>
        </div>
      </div>
    </template>

    <template #audience>
      <span class="vh-phone-q"></span>
      <div class="vh-phone-video">
        <svg viewBox="0 0 100 60" class="vh-phone-frame" preserveAspectRatio="none">
          <rect width="100" height="60" fill="#1A1A2E" />
          <circle
            cx="50"
            cy="22"
            r="10"
            fill="#FFD6E7"
          />
          <path d="M20 56 Q50 42 80 56 L80 60 L20 60 Z" fill="#6A1EBB" opacity="0.5" />
        </svg>
      </div>
      <div class="vh-phone-reacts">
        <button
          v-for="(r, i) in ['😂', '😮', '👏', '🔥']"
          :key="r"
          class="vh-phone-react"
          :class="{ '--tapped': i === 2 }"
          tabindex="-1"
        >
          {{ r }}
        </button>
      </div>
      <div class="vh-phone-hint">
        Tap to react live
      </div>
    </template>
  </PreviewFrame>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import type { SlideType } from '../../data/slideTypeCatalog'
import PreviewFrame from './PreviewFrame.vue'

export default Vue.extend({
  name: 'PreviewVideoHost',
  components: { PreviewFrame },
  props: {
    slide: { type: Object as PropType<SlideType>, required: true },
  },
  data() {
    return {
      stream: [
        { emoji: '👏', x: 18 },
        { emoji: '🔥', x: 38 },
        { emoji: '😂', x: 56 },
        { emoji: '😮', x: 74 },
        { emoji: '❤️', x: 88 },
      ],
    }
  },
})
</script>

<style scoped lang="scss">
.vh-bar {
  display: block; height: 8px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.2);
  width: 70%;
}
.vh-question {
  padding-bottom: 6px;
  border-bottom: 1px dashed color-mix(in srgb, var(--accent) 20%, transparent);
}

.vh-stage {
  display: grid;
  grid-template-columns: 1fr 38px;
  gap: 10px;
  flex: 1;
  align-items: stretch;
}
.vh-video {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background: #1A1A2E;
}
.vh-frame {
  width: 100%;
  height: 100%;
  display: block;
  aspect-ratio: 16 / 9;
}
.vh-pause {
  appearance: none; border: none;
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  width: 36px; height: 36px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  color: rgba(15, 15, 20, 0.85);
  display: grid; place-items: center;
  cursor: pointer;
}
.vh-pause .material-symbols-rounded { font-size: 20px; }
.vh-progress {
  position: absolute;
  bottom: 6px; left: 8px; right: 8px;
  height: 3px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.25);
  overflow: hidden;
}
.vh-progress-fill {
  height: 100%;
  width: 35%;
  background: var(--accent);
  animation: vh-play 4s linear infinite;
}

.vh-stream {
  position: relative;
  width: 38px;
  border-radius: 8px;
  background: rgba(15, 15, 20, 0.04);
  border: 1px dashed rgba(15, 15, 20, 0.1);
  overflow: hidden;
}
.vh-emoji {
  position: absolute;
  bottom: -10px;
  left: 50%;
  font-size: 16px;
  transform: translateX(-50%);
  animation: vh-rise 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
  animation-delay: calc(var(--em-i, 0) * 600ms);
  opacity: 0;
}

/* ---- Audience phone ---- */
.vh-phone-q {
  display: block; height: 8px; border-radius: 999px;
  width: 70%; background: rgba(15, 15, 20, 0.18);
  margin-bottom: 4px;
}
.vh-phone-video {
  border-radius: 6px;
  overflow: hidden;
}
.vh-phone-frame {
  width: 100%;
  display: block;
  aspect-ratio: 5 / 3;
}
.vh-phone-reacts {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 4px;
}
.vh-phone-react {
  appearance: none;
  background: white;
  border: 1px solid rgba(15, 15, 20, 0.1);
  border-radius: 8px;
  padding: 6px 0;
  font-size: 16px;
  cursor: pointer;
}
.vh-phone-react.--tapped {
  background: color-mix(in srgb, var(--accent) 12%, white);
  border-color: var(--accent);
  animation: vh-tap 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
}
.vh-phone-hint {
  text-align: center;
  font-size: 10px; font-weight: 700;
  color: var(--accent);
}

@keyframes vh-play {
  0%   { width: 25%; }
  100% { width: 90%; }
}
@keyframes vh-rise {
  0%, 5%   { transform: translate(-50%, 0) scale(0.6); opacity: 0; }
  18%      { opacity: 1; }
  60%      { transform: translate(-50%, -110px) scale(1); opacity: 1; }
  85%, 100%{ transform: translate(-50%, -130px) scale(0.8); opacity: 0; }
}
@keyframes vh-tap {
  0%, 30%   { transform: scale(1); box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 35%, transparent); }
  45%       { transform: scale(1.2); box-shadow: 0 0 0 8px color-mix(in srgb, var(--accent) 0%, transparent); }
  60%, 100% { transform: scale(1); box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 0%, transparent); }
}

@media (prefers-reduced-motion: reduce) {
  .vh-progress-fill { animation: none; width: 50%; }
  .vh-emoji { animation: none; opacity: 1; transform: translate(-50%, -60px); }
  .vh-phone-react.--tapped { animation: none; }
}
</style>
